import { useCallback } from "react";
import { IGridState } from "@cayo/shared/components/GridContainer/types";
import { ExportMode, exportService, type ExportFormat } from "@cayo/shared/services/export";
import gridDataExportService from "../export/grid-data-export.service";
import { GridContainerProps } from "../grid-model/GridController";

type UseGridExportParams = {
  detailsList: GridContainerProps["scheme"]["detailsList"];
  gridState: IGridState;
  schemeName: GridContainerProps["scheme"]["name"];
};

export const useGridExport = ({ detailsList, gridState, schemeName }: UseGridExportParams) => {
  const buildExportTable = useCallback(
    (mode: ExportMode) =>
      gridDataExportService.buildTable(
        {
          title: detailsList?.id,
          columns: gridState.columns,
          items: gridState.searchResult.items,
          selectedItems: gridState.selectedItems,
        },
        { mode }
      ),
    [detailsList?.id, gridState.columns, gridState.searchResult.items, gridState.selectedItems]
  );

  const formatFileNamePart = (value: string | undefined) =>
    value ? `${value.trim().replace(/\s+/g, "_")}-` : "";

  const buildExportFileName = useCallback(() => {
    const sectionName = formatFileNamePart(schemeName?.trim());
    const viewName = formatFileNamePart(
      detailsList?.name || detailsList?.id || detailsList?.exportConfig?.fileName
    );
    const dateTime = new Date().toISOString().replace("T", "_").replace(/:/g, "_").split(".")[0];

    return `${sectionName}${viewName}${dateTime}`;
  }, [schemeName, detailsList]);

  const getPdfReportTitle = useCallback(() => {
    const sectionTitle = schemeName?.trim();
    const viewTitle = detailsList?.name?.trim() || detailsList?.id?.trim();

    if (sectionTitle && viewTitle && sectionTitle !== viewTitle) {
      return `${sectionTitle} > ${viewTitle}`;
    }

    return viewTitle || sectionTitle;
  }, [detailsList?.id, detailsList?.name, schemeName]);

  const handleExport = useCallback(
    async (format: ExportFormat, mode: ExportMode = "visible") => {
      const table = buildExportTable(mode);

      if (!table.columns.length || !table.rows.length) {
        return;
      }

      await exportService.export({
        format,
        table,
        reportTitle: getPdfReportTitle(),
        fileName: buildExportFileName(),
        mode,
      });
    },
    [buildExportFileName, buildExportTable, getPdfReportTitle]
  );

  return {
    buildExportTable,
    getPdfReportTitle,
    handleExport,
  };
};
